import * as React from "react";
import {Link, useHistory} from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import {SnackbarNotifier} from "../panel/MUI";
import {SvgIcon} from "../../components";
import {tl, emailValidation} from "../../utils";
import * as styles from "./styles.scss";
import {IAPIData, IAPIMessage, IDoubleauthData, ISocialsData} from "./types";
import {LoginPageContext} from "./context";

export const SignInForm = ({
  onDoubleauth,
  host,
  apiData,
  socialsData,
  showFieldsOnMobile = false,
}: {
  onDoubleauth: (data: IDoubleauthData) => void;
  host: string;
  apiData: IAPIData;
  socialsData: ISocialsData | null;
  showFieldsOnMobile?: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"), {
    noSsr: true
  });
  const history = useHistory();

  const [hasBeenSubmitted, setHasBeenSubmitted] = React.useState<boolean>(
    false
  );
  const [disabled, setDisabled] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [message, setMessage] = React.useState<IAPIMessage>(null);

  const {mobileStep, setMobileStep} = React.useContext(LoginPageContext);

  const showError = React.useCallback((value: string) => {
    setMessage({
      message: value,
      messageType: "error",
    });
  }, []);

  const hideError = React.useCallback(() => {
    setMessage(null);
  }, []);

  const isEmailValid = React.useMemo(() => {
    return email && emailValidation(email);
  }, [email]);

  const validate = () => {
    setDisabled(true);

    if (!email || !password) {
      showError(tl("Пожалуйста, заполните выделенные поля"));
      return false;
    }
    if (!isEmailValid) {
      showError(tl("Пожалуйста, укажите корректный e-mail"));
      return false;
    }

    setDisabled(false);
    hideError();
    return true;
  };

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("message")) {
      switch (params.get("message")) {
        case "userdisabled":
          showError(
            tl(
              'Аккаунт заблокирован. Для восстановления доступа обратитесь в поддержку'
            )
          );
          break;
        case "expired":
          showError(
            tl(
              "Ссылка устарела. Зайдите под своим логином и паролем или восстановите пароль"
            )
          );
          break;
        default:
          break;
      }
    }
  }, []);

  React.useEffect(() => {
    if (hasBeenSubmitted) {
      validate();
    }
  }, [email, password, hasBeenSubmitted]);

  const onSignIn = async (evt: React.FormEvent) => {
    evt.preventDefault();
    evt.stopPropagation();

    if (isMobile && mobileStep < 5) {
      setMobileStep(5);
      if (!showFieldsOnMobile) return;
    }

    setHasBeenSubmitted(true);
    if (validate()) {
      setDisabled(true);

      const data = new FormData();
      data.append("service", "at");
      data.append("login", email);
      data.append("password", password);

      const response = await fetch(`${apiData.url}/api/account/login`, {
        method: "post",
        credentials: "include",
        headers: host ? {
          'X-App-Host': host,
        } : {},
        body: data,
      });
      const result = await response.json();

      switch (response.status) {
        case 200:
          getSMSCode(email, password, result);
          break;
        case 400:
          showError(tl("Недопустимое значение логина или пароля"));
          break;
        case 403:
          showError(tl("Неверный логин или пароль"));
          break;
        default:
          showError(`${tl("Неизвестная ошибка")} ${response.status}`);
          break;
      }

      setDisabled(false);
    }
  };

  const getSMSCode = React.useCallback(
    async (login: string, password: string, data: any) => {
      if (data.result) {
        if (data.result === "ok") {
          const redirectUrl = new URL(data.redirectLink);
          window.location.href = `${redirectUrl.pathname}?token=${
            data.token
          }&redirect_uri=${encodeURIComponent(apiData.redirect)}`;
        } else if (data.result === "doubleauth") {
          onDoubleauth({
            timeout: 59,
            phone: data.phone,
            url: "/",
            doubleauth: data.doubleAuth,
            login: login,
            password: password,
          });
          history.push("/login/doubleauth");
        } else {
          showError(tl("Неверный логин или пароль"));
        }
      }
    },
    []
  );

  return (
    <div className={styles.wrap}>
      <form onSubmit={onSignIn} noValidate>
        <Grid container spacing={2} direction="column">
          <Grid item>
            <Box className={styles.title}>{tl("Вход")}</Box>
          </Grid>
          {message ? (
            <Grid item>
              <SnackbarNotifier inline note={message} className="nominwidth" />
            </Grid>
          ) : null}
          <Grid
            item
            className={
              mobileStep !== 5 && !showFieldsOnMobile ? styles.xsHidden : ""
            }
          >
            <TextField
              variant="outlined"
              color="primary"
              fullWidth
              type="email"
              value={email}
              placeholder={tl("E-mail адрес")}
              error={
                hasBeenSubmitted && (!email || (!!password && !isEmailValid))
              }
              onChange={(evt) => {
                setEmail(evt.target.value);
              }}
            />
          </Grid>
          <Grid
            item
            className={
              mobileStep !== 5 && !showFieldsOnMobile ? styles.xsHidden : ""
            }
          >
            <TextField
              variant="outlined"
              color="primary"
              fullWidth
              type="password"
              value={password}
              placeholder={tl("Пароль")}
              error={hasBeenSubmitted && !password}
              onChange={(evt) => {
                setPassword(evt.target.value);
              }}
            />
          </Grid>
          <Grid item container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                data-testid="loginBtn"
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={disabled}
              >
                {tl("Войти")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                data-testid="restorePass"
                variant="text"
                fullWidth
                component={Link}
                to="/login/restore"
              >
                {tl("Восстановить пароль?")}
              </Button>
            </Grid>
            {isMobile && !showFieldsOnMobile &&
              <Grid item xs={12} sm={6}>
                <Button
                  data-testid="restorePass"
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setMobileStep(0);
                  }}
                >
                  {tl("Впервые у нас?")}
                </Button>
              </Grid>
            }
          </Grid>
          {socialsData && (!mobileStep || mobileStep < 5) ? (
            <>
              <Grid item>
                <Box className={styles.title} mt={0.5}>
                  {tl("Войти с помощью")}
                </Box>
              </Grid>
              <Grid item container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<SvgIcon type="vk" />}
                    className="MuiButton-social"
                    href={socialsData.vkontakte}
                  >
                    {tl("ВКонтакте")}
                  </Button>
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    startIcon={<SvgIcon type="fb" />}
                    className="MuiButton-social"
                    href={socialsData.facebook}
                  >
                    Facebook
                  </Button>
                </Grid> */}
              </Grid>
            </>
          ) : null}
        </Grid>
      </form>
    </div>
  );
};
